'use client';

import type { Theme, SxProps } from '@mui/material/styles';
import type { ButtonBaseProps } from '@mui/material/ButtonBase';

import { useState, useCallback } from 'react';
import { usePopover } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import Button, { buttonClasses } from '@mui/material/Button';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { CustomPopover } from 'src/components/custom-popover';
import { useAppContext } from "src/context/app";
import { useEffect } from "react";
import { useBoolean } from 'src/hooks/use-boolean';
import type { IOrganisation } from 'src/types/nya/common';
import { ALLA_ORG, ärAllaId } from 'src/context/app/alla-organisationer';
import { useAuthContext } from 'src/auth/hooks';
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import LäggTillOrganisationDialog from 'src/sections/organisation/LäggTillOrganisationDialog';

// ----------------------------------------------------------------------

export { ALLA_ORG };

export type OrganisationPopoverProps = ButtonBaseProps & {
  organisationer?: IOrganisation[];
};

export function OrganisationPopover({ organisationer = [], sx, ...other }: OrganisationPopoverProps) {
  const mediaQuery = 'sm';

  // Väljaren visar valet som det är, inklusive attrappen Privatperson — därför
  // `valdOrganisation` och inte `organisation` (som är undefined i det läget).
  const { valdOrganisation: organisation, uppdateraValdOrganisation } = useAppContext();
  const { anvandare, checkUserSession, loading } = useAuthContext();
  const router = useRouter();
  const läggTill = useBoolean();

  const handleOrgCreated = useCallback(
    async (nyskapad?: IOrganisation) => {
      await checkUserSession?.();
      if (nyskapad) {
        uppdateraValdOrganisation(nyskapad);
        router.push(paths.dashboard.komIgång);
      }
    },
    [checkUserSession, uppdateraValdOrganisation, router]
  );

  const ärAlla = ärAllaId(organisation?.id);
  const anvandarensNamn = [anvandare?.namn?.förnamn, anvandare?.namn?.efternamn]
    .filter(Boolean)
    .join(' ')
    .trim();
  const hubbEtikett = anvandarensNamn || 'Alla organisationer';
  /**
   * Underraden sätts bara när användaren faktiskt har föreningar. "Alla
   * organisationer" under namnet på någon som inte är med i en enda vore ett
   * löfte om innehåll som inte finns.
   */
  const hubbUnderrad = organisationer.length > 0 ? 'Alla organisationer' : undefined;

  const { open, anchorEl, onClose, onOpen } = usePopover();

  useEffect(() => {
    // Vänta tills sessionen har laddat klart innan vi utvärderar valet.
    if (loading) {
      return;
    }

    // Hubben är alltid ett giltigt val.
    if (ärAllaId(organisation?.id)) {
      return;
    }

    // Inget val gjort, eller ett sparat val som inte längre finns bland
    // användarens föreningar. Vad vi faller tillbaka på beror på hur många
    // föreningar det finns att välja mellan:
    //
    // En enda: välj den. Den som bara har en organisation har inget att välja
    // mellan, och hubben — som finns för att hålla isär flera föreningar — gav
    // då en lista där varenda rad bar samma namn, i stället för föreningens
    // egen översikt.
    //
    // Flera, eller inga alls: hubben. Ett godtyckligt förstaval hade i
    // praktiken dolt de övriga föreningarna för den som är med i flera, och
    // utan föreningar är hubbens tomläge det som ber om att man lägger till en.
    const valetFinnsKvar =
      organisation && organisationer.some((org) => org.id === organisation.id);
    if (!valetFinnsKvar) {
      uppdateraValdOrganisation(organisationer.length === 1 ? organisationer[0] : ALLA_ORG);
    }
  }, [organisation, organisationer, uppdateraValdOrganisation, loading]);

  const handleChangeWorkspace = useCallback(
    (newValue: IOrganisation) => {
      uppdateraValdOrganisation(newValue);
      onClose();
      // Ingen omladdning av fönstret: AppContextProvider tömmer SWR-cachen och
      // DashboardLayout monterar om innehållet, så sidan och menyn hämtar sitt
      // för den nya föreningen.
    },
    [onClose, uppdateraValdOrganisation]
  );

  const buttonBg: SxProps<Theme> = {
    height: 1,
    zIndex: -1,
    opacity: 0,
    content: "''",
    borderRadius: 1,
    position: 'absolute',
    visibility: 'hidden',
    bgcolor: 'action.hover',
    width: 'calc(100% + 8px)',
    transition: (theme) =>
      theme.transitions.create(['opacity', 'visibility'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
      }),
    ...(open && {
      opacity: 1,
      visibility: 'visible',
    }),
  };

  /** Popoverns fotknappar: vänsterställd ikon + etikett. */
  const knappSx: SxProps<Theme> = {
    gap: 2,
    justifyContent: 'flex-start',
    fontWeight: 'fontWeightMedium',
    [`& .${buttonClasses.startIcon}`]: {
      m: 0,
      width: 24,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  };

  const renderButton = () => (
    <ButtonBase
      disableRipple
      onClick={onOpen}
      sx={[
        {
          py: 0.5,
          gap: { xs: 0.5, [mediaQuery]: 1 },
          '&::before': buttonBg,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      {ärAlla && (
        <Iconify
          icon="solar:widget-5-bold-duotone"
          sx={{ width: 20, height: 20, color: 'text.secondary' }}
        />
      )}

      <Box
        component="span"
        sx={{ typography: 'subtitle2', display: { xs: 'none', [mediaQuery]: 'inline-flex' } }}
      >
        {ärAlla ? hubbEtikett : organisation?.organisationsNamn}
      </Box>

      {ärAlla && hubbUnderrad && (
        <Label
          color="default"
          sx={{
            height: 22,
            cursor: 'inherit',
            display: { xs: 'none', [mediaQuery]: 'inline-flex' },
          }}
        >
          {hubbUnderrad}
        </Label>
      )}

      {!ärAlla && organisation?.organisationsNummer && (
        <Label
          color={'info'}
          sx={{
            height: 22,
            cursor: 'inherit',
            display: { xs: 'none', [mediaQuery]: 'inline-flex' },
          }}
        >
          {organisation?.organisationsNummer}
        </Label>
      )}

      <Iconify width={16} icon="carbon:chevron-sort" sx={{ color: 'text.disabled' }} />
    </ButtonBase>
  );

  const renderMenuList = () => (
    <CustomPopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      slotProps={{
        arrow: { placement: 'top-left' },
        paper: { sx: { mt: 0.5, ml: -1.55, width: 240 } },
      }}
    >
      <Scrollbar sx={{ maxHeight: 240 }}>
        <MenuList>
          <MenuItem
            selected={ärAlla}
            onClick={() => handleChangeWorkspace(ALLA_ORG)}
            sx={{ height: 48 }}
          >
            <Iconify
              icon="solar:widget-5-bold-duotone"
              sx={{ width: 24, height: 24, mr: 1, color: 'text.secondary' }}
            />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                noWrap
                component="span"
                variant="body2"
                sx={{ display: 'block', fontWeight: 'fontWeightMedium' }}
              >
                {hubbEtikett}
              </Typography>
              {hubbUnderrad && (
                <Typography
                  noWrap
                  component="span"
                  variant="caption"
                  sx={{ display: 'block', color: 'text.secondary' }}
                >
                  {hubbUnderrad}
                </Typography>
              )}
            </Box>
          </MenuItem>

          {organisationer.length > 0 && <Divider sx={{ my: 0.5, borderStyle: 'dashed' }} />}

          {organisationer.map((org) => (
            <MenuItem
              key={org.id}
              selected={org.id === organisation?.id}
              onClick={() => handleChangeWorkspace(org)}
              sx={{ height: 48 }}
            >
              {/*<Avatar alt={org.organisationsNamn} src={org.logo} sx={{ width: 24, height: 24 }} />*/}

              <Typography
                noWrap
                component="span"
                variant="body2"
                sx={{ flexGrow: 1, fontWeight: 'fontWeightMedium' }}
              >
                {org.organisationsNamn}
              </Typography>

              {/*<Label color={org.plan === 'Free' ? 'default' : 'info'}>{org.plan}</Label>*/}
            </MenuItem>
          ))}
        </MenuList>
      </Scrollbar>

      <Divider sx={{ my: 0.5, borderStyle: 'dashed' }} />

      {/*
        Kontonivån: att registrera en ny förening eller gå med i en befintlig är
        inte något man gör *i* en förening, så de bor på hubben i stället för i
        sidomenyn. Härifrån räcker en väg dit.
      */}
      <Button
        fullWidth
        startIcon={<Iconify width={18} icon="solar:buildings-3-bold-duotone" />}
        onClick={() => {
          onClose();
          router.push(paths.dashboard.organisation.mina);
        }}
        sx={knappSx}
      >
        Mina organisationer
      </Button>

      <Button
        fullWidth
        startIcon={<Iconify width={18} icon="solar:stars-minimalistic-bold" />}
        onClick={() => {
          onClose();
          router.push(paths.dashboard.demo);
        }}
        sx={{ ...knappSx, color: 'primary.main' }}
      >
        Prova demoläge
      </Button>

      <Button
        fullWidth
        startIcon={<Iconify width={18} icon="mingcute:add-line" />}
        onClick={() => {
          onClose();
          läggTill.onTrue();
        }}
        sx={knappSx}
      >
        Lägg till organisation
      </Button>
    </CustomPopover>
  );

  return (
    <>
      {renderButton()}
      {renderMenuList()}
      <LäggTillOrganisationDialog
        open={läggTill.value}
        onClose={läggTill.onFalse}
        onCreated={handleOrgCreated}
      />
    </>
  );
}
